// Competitive Mode Components: Leaderboards, Tournaments, Rankings
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { featureFlags, userHasPremiumAccess } from "@/config/featureFlags";
import PremiumUpgradeDialog from "@/components/PremiumUpgradeDialog";
import {
  Trophy,
  Crown,
  Medal,
  Calendar,
  Users,
  Award,
  Zap,
  Target,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useTournamentsListRealtime } from "@/hooks/useTournamentRealtime";
import {
  formatDateInTimezone,
  formatTimeRemaining,
  type TimezoneOption,
} from "@/utils/timezone";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ====================================
// TYPES
// ====================================
interface LeaderboardEntry {
  user_id: string;
  rank: number;
  score: number;
  problems_solved: number;
  accuracy: number;
  xp_earned: number;
  users?: {
    username: string;
    display_name: string;
  };
}

export interface UserRanking {
  user_id: string;
  global_rank: number | null;
  rating: number;
  rank_tier: string;
  total_score: number;
  competitions_entered: number;
  competitions_won: number;
  win_rate: number;
  highest_rating: number;
}

interface Tournament {
  id: string;
  name: string;
  description: string;
  tournament_type: string;
  status: string;
  start_time: string;
  end_time: string;
  max_participants: number | null;
  prize_pool_xp: number;
  technique_id: string | null;
  difficulty: string | null;
  level: number | null;
  participant_count?: number;
  user_participating?: boolean;
}

// ====================================
// RANK TIER BADGE
// ====================================
export function RankTierBadge({
  tier,
  rating,
}: {
  tier: string;
  rating: number;
}) {
  const getTierColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case "master":
        return "from-purple-500 to-pink-600";
      case "diamond":
        return "from-cyan-400 to-blue-600";
      case "platinum":
        return "from-gray-300 to-gray-500";
      case "gold":
        return "from-yellow-400 to-yellow-600";
      case "silver":
        return "from-gray-400 to-gray-600";
      case "bronze":
        return "from-amber-600 to-amber-800";
      default:
        return "from-gray-400 to-gray-600";
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier.toLowerCase()) {
      case "master":
        return "👑";
      case "diamond":
        return "💎";
      case "platinum":
        return "⚪";
      case "gold":
        return "🥇";
      case "silver":
        return "🥈";
      case "bronze":
        return "🥉";
      default:
        return "🎯";
    }
  };

  return (
    <div
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${getTierColor(tier)} text-white font-semibold shadow-lg`}
    >
      <span className="text-xl">{getTierIcon(tier)}</span>
      <div>
        <div className="text-sm uppercase">{tier}</div>
        <div className="text-xs opacity-90">{rating} Rating</div>
      </div>
    </div>
  );
}

// ====================================
// USER RANKING CARD
// ====================================
export function UserRankingCard({ ranking }: { ranking: UserRanking | null }) {
  if (!ranking) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">
            Complete competitions to get ranked!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Your Ranking</span>
          <RankTierBadge tier={ranking.rank_tier} rating={ranking.rating} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {ranking.global_rank ? `#${ranking.global_rank}` : "Unranked"}
            </div>
            <p className="text-xs text-gray-500 mt-1">Global Rank</p>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {ranking.competitions_won}
            </div>
            <p className="text-xs text-gray-500 mt-1">Wins</p>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {ranking.win_rate.toFixed(0)}%
            </div>
            <p className="text-xs text-gray-500 mt-1">Win Rate</p>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {ranking.highest_rating}
            </div>
            <p className="text-xs text-gray-500 mt-1">Peak Rating</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              Total Score
            </span>
            <span className="font-semibold">
              {ranking.total_score.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mt-2">
            <span className="text-gray-600 dark:text-gray-400">
              Competitions Entered
            </span>
            <span className="font-semibold">
              {ranking.competitions_entered}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ====================================
// LEADERBOARD COMPONENT
// ====================================
export function Leaderboard() {
  const [period, setPeriod] = useState<
    "all_time" | "weekly" | "monthly" | "daily"
  >("weekly");
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEntry, setUserEntry] = useState<LeaderboardEntry | null>(null);

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    // Calculate period_start based on period
    let periodStart: string;
    const now = new Date();

    switch (period) {
      case "daily":
        periodStart = now.toISOString().split("T")[0];
        break;
      case "weekly":
        const monday = new Date(now);
        monday.setDate(now.getDate() - now.getDay() + 1);
        periodStart = monday.toISOString().split("T")[0];
        break;
      case "monthly":
        periodStart = new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .split("T")[0];
        break;
      default:
        periodStart = "2020-01-01";
    }

    const { data, error } = await supabase
      .from("leaderboards")
      .select(
        `
        *,
        users!user_id (username)
      `,
      )
      .eq("period", period)
      .eq("period_start", periodStart)
      .order("rank", { ascending: true })
      .limit(100);

    if (!error && data) {
      setLeaderboard(data as any);

      // Find current user's entry
      if (user) {
        const userEntry = data.find((entry: any) => entry.user_id === user.id);
        setUserEntry((userEntry as any) || null);
      }
    }

    setLoading(false);
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-gray-500 font-semibold">#{rank}</span>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            <CardTitle>Leaderboard</CardTitle>
          </div>
        </div>
        <Tabs
          value={period}
          onValueChange={(v) => setPeriod(v as any)}
          className="mt-4"
        >
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="daily">Today</TabsTrigger>
            <TabsTrigger value="weekly">Week</TabsTrigger>
            <TabsTrigger value="monthly">Month</TabsTrigger>
            <TabsTrigger value="all_time">All Time</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
              />
            ))}
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Trophy className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>No rankings yet for this period.</p>
            <p className="text-sm mt-2">Be the first to compete!</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {leaderboard.slice(0, 10).map((entry, index) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                    entry.user_id === userEntry?.user_id
                      ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500"
                      : index < 3
                        ? "bg-yellow-50/50 dark:bg-yellow-900/10"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <div className="flex-shrink-0 w-12 text-center">
                    {getRankIcon(entry.rank)}
                  </div>

                  <div className="flex-grow min-w-0">
                    <div className="font-semibold truncate">
                      {entry.users?.display_name ||
                        entry.users?.username ||
                        "Anonymous"}
                      {entry.user_id === userEntry?.user_id && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                          (You)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {entry.problems_solved} problems •{" "}
                      {entry.accuracy.toFixed(1)}% accuracy
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {entry.score.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {entry.xp_earned} XP
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {userEntry && userEntry.rank > 10 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 mb-2">Your Position:</p>
                <div className="flex items-center gap-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500">
                  <div className="flex-shrink-0 w-12 text-center">
                    <span className="text-gray-500 font-semibold">
                      #{userEntry.rank}
                    </span>
                  </div>
                  <div className="flex-grow">
                    <div className="font-semibold">You</div>
                    <div className="text-xs text-gray-500">
                      {userEntry.problems_solved} problems •{" "}
                      {userEntry.accuracy.toFixed(1)}% accuracy
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {userEntry.score.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {userEntry.xp_earned} XP
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ====================================
// TOURNAMENT CARD
// ====================================
export function TournamentCard({
  tournament,
  onUpdate,
}: {
  tournament: Tournament;
  onUpdate?: () => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremiumUser = userHasPremiumAccess(user);
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedTimezone, setSelectedTimezone] =
    useState<TimezoneOption>("CST");

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "upcoming":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "completed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "weekly_challenge":
        return <Calendar className="w-5 h-5" />;
      case "monthly_championship":
        return <Crown className="w-5 h-5" />;
      case "special_event":
        return <Zap className="w-5 h-5" />;
      case "technique_focused":
        return <Target className="w-5 h-5" />;
      default:
        return <Trophy className="w-5 h-5" />;
    }
  };

  const handleJoinTournament = async () => {
    setIsJoining(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc("join_tournament", {
        p_tournament_id: tournament.id,
      });

      if (error) {
        console.error("Join tournament error:", error);
        setError(error.message || "Failed to join tournament");
        setIsJoining(false);
        return;
      }

      if (!data.success) {
        setError(data.error || "Failed to join tournament");
        setIsJoining(false);
        return;
      }

      // Success - refresh the list
      if (onUpdate) {
        onUpdate();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveTournament = async () => {
    setIsLeaving(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc("leave_tournament", {
        p_tournament_id: tournament.id,
      });

      if (error) {
        console.error("Leave tournament error:", error);
        setError(error.message || "Failed to leave tournament");
        setIsLeaving(false);
        return;
      }

      if (!data.success) {
        setError(data.error || "Failed to leave tournament");
        setIsLeaving(false);
        return;
      }

      // Success - refresh the list
      if (onUpdate) {
        onUpdate();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLeaving(false);
    }
  };

  const handleContinueCompetition = async () => {
    setIsStarting(true);
    setError(null);
    try {
      // Start tournament session
      const response = await api.startTournamentSession(tournament.id);
      console.log("Tournament session started:", response);

      // Navigate to tournament arena with session ID and tournament ID
      navigate(
        `/tournament?session=${response.session.id}&tournament=${tournament.id}`,
      );
    } catch (err) {
      console.error("Failed to start tournament session:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to start tournament session";
      setError(errorMessage);
      setIsStarting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateInTimezone(dateString, selectedTimezone);
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {getTypeIcon(tournament.tournament_type)}
            <CardTitle className="text-lg">{tournament.name}</CardTitle>
          </div>
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(tournament.status)}`}
          >
            {tournament.status}
          </span>
        </div>
        <CardDescription>{tournament.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Timezone Selector */}
          <div className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50 p-2 rounded">
            <span className="text-gray-600 dark:text-gray-400 text-xs">
              Time Zone:
            </span>
            <Select
              value={selectedTimezone}
              onValueChange={(value) =>
                setSelectedTimezone(value as TimezoneOption)
              }
            >
              <SelectTrigger className="w-[140px] h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CST">CST (UTC-6)</SelectItem>
                <SelectItem value="EST">EST (UTC-5)</SelectItem>
                <SelectItem value="PST">PST (UTC-8)</SelectItem>
                <SelectItem value="local">Local Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Start Time</span>
            <span className="font-medium">
              {formatDate(tournament.start_time)}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">End Time</span>
            <span className="font-medium">
              {formatDate(tournament.end_time)}
            </span>
          </div>

          {/* Time Remaining for Active Tournaments */}
          {tournament.status === "active" && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Time Remaining
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {formatTimeRemaining(tournament.end_time)}
              </span>
            </div>
          )}

          {tournament.level && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Tournament Level
              </span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                Level {tournament.level}
              </span>
            </div>
          )}

          {tournament.difficulty && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Difficulty
              </span>
              <span className="font-medium capitalize">
                {tournament.difficulty}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Prize Pool</span>
            <span className="font-semibold text-yellow-600 dark:text-yellow-500 flex items-center gap-1">
              <Award className="w-4 h-4" />
              {tournament.prize_pool_xp} XP
            </span>
          </div>

          {tournament.participant_count !== undefined && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Participants
              </span>
              <span className="font-medium flex items-center gap-1">
                <Users className="w-4 h-4" />
                {tournament.participant_count}
                {tournament.max_participants &&
                  ` / ${tournament.max_participants}`}
              </span>
            </div>
          )}

          {error && (
            <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-800 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Join button - show for upcoming OR active tournaments (if not participating) */}
          {(tournament.status === "upcoming" ||
            tournament.status === "active") &&
            !tournament.user_participating && (
              featureFlags.CHARGEBEE_BILLING && !isPremiumUser ? (
                <Button
                  onClick={() => setShowUpgradeDialog(true)}
                  className="w-full mt-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                >
                  Upgrade to Join Tournaments
                </Button>
              ) : (
                <Button
                  onClick={handleJoinTournament}
                  disabled={isJoining}
                  className="w-full mt-4"
                >
                  {isJoining
                    ? "Joining..."
                    : tournament.status === "active"
                      ? "Join Tournament (In Progress)"
                      : "Join Tournament"}
                </Button>
              )
            )}

          <PremiumUpgradeDialog
            open={showUpgradeDialog}
            onOpenChange={setShowUpgradeDialog}
            feature="Tournaments"
          />

          {/* Registered + Leave button - show for upcoming tournaments */}
          {tournament.status === "upcoming" &&
            tournament.user_participating && (
              <div className="space-y-2 mt-4">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded text-center text-sm text-green-800 dark:text-green-400 font-medium">
                  ✓ Registered
                </div>
                <Button
                  onClick={handleLeaveTournament}
                  disabled={isLeaving}
                  variant="outline"
                  className="w-full"
                >
                  {isLeaving ? "Leaving..." : "Leave Tournament"}
                </Button>
              </div>
            )}

          {/* Continue Competition button - show for active tournaments if participating */}
          {tournament.status === "active" && tournament.user_participating && (
            <Button
              className="w-full mt-4"
              variant="default"
              onClick={handleContinueCompetition}
              disabled={isStarting}
            >
              {isStarting ? "Starting..." : "Continue Competition"}
            </Button>
          )}

          {tournament.status === "completed" && (
            <Button
              className="w-full mt-4"
              variant="outline"
              onClick={() => navigate(`/tournament-results/${tournament.id}`)}
            >
              View Results
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ====================================
// TOURNAMENTS LIST
// ====================================
export function TournamentsList() {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [filter, setFilter] = useState<
    "all" | "upcoming" | "active" | "completed"
  >("active");
  const [loading, setLoading] = useState(true);

  const fetchTournaments = useCallback(async () => {
    setLoading(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    let query = supabase
      .from("tournaments")
      .select("*, participant_count:tournament_participants(count)");

    if (filter !== "all") {
      query = query.eq("status", filter);
    }

    query = query.order("start_time", {
      ascending: filter === "completed" ? false : true,
    });

    const { data, error } = await query;

    if (!error && data) {
      // Check if user is participating in each tournament
      const tournamentsWithUserStatus = await Promise.all(
        data.map(async (tournament: any) => {
          if (user) {
            const { data: participation } = await supabase
              .from("tournament_participants")
              .select("id")
              .eq("tournament_id", tournament.id)
              .eq("user_id", user.id)
              .maybeSingle();

            return {
              ...tournament,
              participant_count: tournament.participant_count[0]?.count || 0,
              user_participating: !!participation,
            };
          }
          return {
            ...tournament,
            participant_count: tournament.participant_count[0]?.count || 0,
          };
        }),
      );

      setTournaments(tournamentsWithUserStatus);
    }

    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  // Add realtime subscription for live updates
  useTournamentsListRealtime(fetchTournaments);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant={filter === "active" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("active")}
        >
          Active
        </Button>
        <Button
          variant={filter === "upcoming" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("upcoming")}
        >
          Upcoming
        </Button>
        <Button
          variant={filter === "completed" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("completed")}
        >
          Completed
        </Button>
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
        >
          All
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
            />
          ))}
        </div>
      ) : tournaments.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Trophy className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No {filter !== "all" ? filter : ""} tournaments found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tournaments.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              onUpdate={fetchTournaments}
            />
          ))}
        </div>
      )}
    </div>
  );
}
